// src/components/ShareAccessModal.jsx
import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

/**
 * Modal pour partager l'accès d'une collection avec un expert
 * VERSION SANS COLONNES SNAPSHOT (grantee_name, grantee_role, etc.)
 */
export default function ShareAccessModal({ open, onClose, collectionId, collectionTitle }) {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [experts, setExperts] = useState([]);
  const [selectedExpertId, setSelectedExpertId] = useState("");
  const [canEdit, setCanEdit] = useState(false);
  const [shareLocations, setShareLocations] = useState(false); // opt-in : partager les lieux de conservation
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [existingShares, setExistingShares] = useState([]);
  const [confirmRevoke, setConfirmRevoke] = useState(null);


  const computeDisplayName = (p) => {
    if (!p) return "Utilisateur";
    const fn = (p?.first_name || "").trim();
    const ln = (p?.last_name || "").trim();
    const full = `${fn} ${ln}`.trim();
    return full || (p?.company || "").trim() || "Utilisateur";
  };

  // ✅ FIX: annulation des effets async pour éviter setState après unmount/changement d'id
  useEffect(() => {
    if (!open || !user?.id) return;

    if (!collectionId) {
      setError("Erreur: ID de collection manquant");
      return;
    }

    let cancelled = false;

    const loadData = async () => {
      try {
        setLoading(true);
        setError("");
        setSuccess("");

        // 1) Experts
        const { data: expertsData, error: expertsError } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, company, advisor_notes, role, plan")
          .eq("role", "expert")
          .order("first_name");

        if (expertsError) throw expertsError;
        if (!cancelled) setExperts(Array.isArray(expertsData) ? expertsData : []);

        // 2a) Collection shares
        const { data: collectionShares, error: collectionError } = await supabase
          .from("collection_permissions")
          .select("id, grantee_id, collection_id, can_view, can_edit, created_at")
          .eq("client_id", user.id)
          .eq("collection_id", collectionId);

        if (collectionError) throw collectionError;

        // 2b) Global shares
        const { data: globalShares, error: globalError } = await supabase
          .from("collection_permissions")
          .select("id, grantee_id, collection_id, can_view, can_edit, created_at")
          .eq("client_id", user.id)
          .is("collection_id", null);

        if (globalError) throw globalError;

        const sharesData = [
          ...(Array.isArray(collectionShares) ? collectionShares : []),
          ...(Array.isArray(globalShares) ? globalShares : []),
        ];

        if (sharesData.length === 0) {
          if (!cancelled) setExistingShares([]);
          return;
        }

        // 3) Enrich - grantees profiles
        const granteeIds = [...new Set(sharesData.map((r) => r.grantee_id).filter(Boolean))];

        const { data: granteesProfiles, error: gpErr } =
          granteeIds.length > 0
            ? await supabase
                .from("profiles")
                .select("id, first_name, last_name, company, role, plan")
                .in("id", granteeIds)
            : { data: [], error: null };

        if (gpErr) {
          console.warn("⚠️ Could not load grantee profiles:", gpErr);
        }

        const profById = Object.fromEntries(
          (Array.isArray(granteesProfiles) ? granteesProfiles : []).map((p) => [p.id, p])
        );

        const enriched = sharesData.map((share) => {
          const p = profById[share.grantee_id];
          return {
            ...share,
            expert: p || null,
            displayName: computeDisplayName(p),
            displayRole: p?.role || "unknown",
            displayPlan: p?.plan || "unknown",
            displayCompany: p?.company || null,
            scope: share.collection_id ? "collection" : "global",
          };
        });

        if (!cancelled) setExistingShares(enriched);
      } catch (e) {
        if (!cancelled) setError(`Erreur: ${e?.message || String(e)}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [open, user?.id, collectionId]);

  const handleShare = async () => {
    if (!selectedExpertId) {
      setError("Veuillez sélectionner un expert");
      return;
    }

    if (!collectionId) {
      setError("Erreur: ID de collection manquant");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const { data: existing } = await supabase
        .from("collection_permissions")
        .select("id")
        .eq("client_id", user.id)
        .eq("grantee_id", selectedExpertId)
        .eq("collection_id", collectionId)
        .maybeSingle();

      if (existing) {
        setError("Cette collection est déjà partagée avec cet expert");
        setLoading(false);
        return;
      }

      const { error: insertError } = await supabase.from("collection_permissions").insert({
        client_id: user.id,
        grantee_id: selectedExpertId,
        collection_id: collectionId,
        can_view: true,
        can_edit: canEdit,
        can_view_location: shareLocations,
      });

      if (insertError) throw insertError;

      // Sync Kanban
      try {
        await supabase
          .from("expert_service_requests")
          .update({ status: "accepted", updated_at: new Date().toISOString() })
          .eq("client_id", user.id)
          .eq("expert_id", selectedExpertId)
          .in("status", ["sharingwaiting"]);
      } catch (e) {
        console.warn("⚠️ Kanban sync failed:", e);
      }

      setSuccess("Collection partagée avec succès !");
      setSelectedExpertId("");
      setCanEdit(false);
      setShareLocations(false);

      // Reload (simple)
      const [collResult, globalResult] = await Promise.all([
        supabase
          .from("collection_permissions")
          .select("id, grantee_id, collection_id, can_view, can_edit, created_at")
          .eq("client_id", user.id)
          .eq("collection_id", collectionId),
        supabase
          .from("collection_permissions")
          .select("id, grantee_id, collection_id, can_view, can_edit, created_at")
          .eq("client_id", user.id)
          .is("collection_id", null),
      ]);

      const allShares = [...(collResult.data || []), ...(globalResult.data || [])];
      const granteeIds = [...new Set(allShares.map((r) => r.grantee_id).filter(Boolean))];

      const { data: profiles } =
        granteeIds.length > 0
          ? await supabase
              .from("profiles")
              .select("id, first_name, last_name, company, role, plan")
              .in("id", granteeIds)
          : { data: [] };

      const profById = Object.fromEntries((profiles || []).map((p) => [p.id, p]));

      setExistingShares(
        allShares.map((share) => ({
          ...share,
          expert: profById[share.grantee_id] || null,
          displayName: computeDisplayName(profById[share.grantee_id]),
          displayRole: profById[share.grantee_id]?.role || "unknown",
          displayPlan: profById[share.grantee_id]?.plan || "unknown",
          displayCompany: profById[share.grantee_id]?.company || null,
          scope: share.collection_id ? "collection" : "global",
        }))
      );
    } catch (e) {
      console.error("❌ Share error:", e);
      setError(`Erreur: ${e?.message || String(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRevoke = async () => {
    if (!confirmRevoke) return;

    try {
      setLoading(true);
      setError("");

      const { error: deleteError } = await supabase
        .from("collection_permissions")
        .delete()
        .eq("id", confirmRevoke.id)
        .eq("client_id", user.id);

      if (deleteError) throw deleteError;

      setSuccess("Accès révoqué avec succès");
      setExistingShares((prev) => prev.filter((s) => s.id !== confirmRevoke.id));
      setConfirmRevoke(null);
    } catch (e) {
      console.error("❌ Revoke error:", e);
      setError(`Erreur: ${e?.message || String(e)}`);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  if (!collectionId) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0, 0, 0, 0.4)",
        padding: "16px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "600px",
          borderRadius: "2px",
          background: "#fff",
          padding: "32px",
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 300,
                letterSpacing: "-0.02em",
                marginBottom: "8px",
                fontFamily: "Georgia, serif",
              }}
            >
              Partager un accès
            </h2>
            <p style={{ fontSize: "14px", color: "#6b6b6b", fontFamily: "system-ui, sans-serif" }}>
              {collectionTitle || "Collection"}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              color: "#999",
              cursor: "pointer",
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "2px",
              color: "#991b1b",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px",
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "2px",
              color: "#166534",
              fontSize: "14px",
            }}
          >
            {success}
          </div>
        )}

        <div style={{ display: "grid", gap: "16px" }}>
          <label
            style={{
              fontSize: "13px",
              fontWeight: 500,
              color: "#1a1a1a",
              fontFamily: "system-ui, sans-serif",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Choisir un expert
          </label>

          <select
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid #e5e3df",
              borderRadius: "2px",
              fontSize: "14px",
              fontFamily: "system-ui, sans-serif",
              background: "#fff",
            }}
            value={selectedExpertId}
            onChange={(e) => setSelectedExpertId(e.target.value)}
            disabled={loading}
          >
            <option value="">Sélectionner...</option>
            {experts.map((e) => (
              <option key={e.id} value={e.id}>
                {computeDisplayName(e)}
              </option>
            ))}
          </select>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              color: "#4a4a4a",
              fontFamily: "system-ui, sans-serif",
            }}
          >
            <input
              type="checkbox"
              checked={canEdit}
              onChange={(e) => setCanEdit(e.target.checked)}
              disabled={loading}
            />
            Autoriser l&apos;édition
          </label>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              color: "#4a4a4a",
              fontFamily: "system-ui, sans-serif",
              marginTop: "8px",
            }}
          >
            <input
              type="checkbox"
              checked={shareLocations}
              onChange={(e) => setShareLocations(e.target.checked)}
              disabled={loading}
            />
            Partager mes lieux de conservation (adresses, placements, photos de contexte)
          </label>

          <button
            onClick={handleShare}
            disabled={loading || !selectedExpertId}
            style={{
              padding: "12px 24px",
              background: "#1a1a1a",
              color: "#fff",
              border: "none",
              borderRadius: "2px",
              fontSize: "14px",
              fontWeight: 500,
              cursor: loading || !selectedExpertId ? "not-allowed" : "pointer",
              opacity: loading || !selectedExpertId ? 0.5 : 1,
              fontFamily: "system-ui, sans-serif",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {loading ? "..." : "PARTAGER"}
          </button>
        </div>

        <div style={{ marginTop: "32px" }}>
          <div
            style={{
              fontSize: "13px",
              fontWeight: 500,
              color: "#1a1a1a",
              marginBottom: "12px",
              fontFamily: "system-ui, sans-serif",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Accès existants
          </div>

          {existingShares.length === 0 ? (
            <div style={{ fontSize: "14px", color: "#999", fontFamily: "system-ui, sans-serif" }}>
              Aucun accès partagé.
            </div>
          ) : (
            <div style={{ display: "grid", gap: "8px" }}>
              {existingShares.map((s) => (
                <div
                  key={s.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px",
                    border: "1px solid #e5e3df",
                    borderRadius: "2px",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: "14px", fontWeight: 500, color: "#1a1a1a", fontFamily: "system-ui, sans-serif" }}>
                      {s.displayName}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b6b6b", fontFamily: "system-ui, sans-serif" }}>
                      {s.displayRole} • {s.scope === "global" ? "Global" : "Collection"} •{" "}
                      {s.can_edit ? "Édition" : "Lecture"}
                    </div>
                  </div>
                  <button
                    style={{
                      fontSize: "13px",
                      color: "#991b1b",
                      background: "none",
                      border: "none",
                      cursor: loading ? "not-allowed" : "pointer",
                      padding: "4px 8px",
                      fontFamily: "system-ui, sans-serif",
                    }}
                    onClick={() => setConfirmRevoke({ id: s.id, name: s.displayName })}
                    disabled={loading}
                  >
                    Révoquer
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {confirmRevoke && (
          <div
            style={{
              marginTop: "20px",
              padding: "16px",
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: "2px",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#92400e",
                marginBottom: "12px",
                fontFamily: "system-ui, sans-serif",
              }}
            >
              Confirmer la révocation pour <strong>{confirmRevoke.name}</strong> ?
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={handleConfirmRevoke}
                disabled={loading}
                style={{
                  padding: "8px 16px",
                  background: "#991b1b",
                  color: "#fff",
                  border: "none",
                  borderRadius: "2px",
                  fontSize: "13px",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.5 : 1,
                  fontFamily: "system-ui, sans-serif",
                }}
              >
                Confirmer
              </button>
              <button
                onClick={() => setConfirmRevoke(null)}
                disabled={loading}
                style={{
                  padding: "8px 16px",
                  background: "#fff",
                  color: "#1a1a1a",
                  border: "1px solid #e5e3df",
                  borderRadius: "2px",
                  fontSize: "13px",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontFamily: "system-ui, sans-serif",
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}